/**
 * This module defines methods that create Promises out of useful APIs that use
 * the callback pattern.
 */

import { getGlobalInterruptPromise } from './globalInterrupt'

export class TimeoutError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'TimeoutError'
  }
}

function wait(timeMs: number): Promise<void> {
  return new Promise((resolve, reject) => {
    const timeoutId = setTimeout(resolve, timeMs)

    getGlobalInterruptPromise().then((exception) => {
      clearTimeout(timeoutId)
      reject(exception)
    })
  })
}

async function throwIfTimeout(message: string, timeMs: number): Promise<never> {
  await wait(timeMs)
  throw new TimeoutError(message)
}

function getNullPromise(): Promise<void> {
  return new Promise((resolve, reject) => {
    getGlobalInterruptPromise().then((exception) => {
      reject(exception)
    })
    resolve()
  })
}

/**
 * Observe the subtree at rootNode, and resolve the promise if we observe
 * that a node matching nodeCondition has been added.
 *
 * The observer is removed once the first time the condition is met.
 */
function getDomPromise(
  rootNode: Node,
  nodeCondition: (node: Node) => boolean,
): Promise<Node> {
  return new Promise((resolve, reject) => {
    const domObserver = new MutationObserver((mutations, observer) => {
      if (mutations) {
        mutations.forEach((mutation) => {
          if (mutation.addedNodes) {
            mutation.addedNodes.forEach((node) => {
              if (nodeCondition(node)) {
                observer.disconnect()
                resolve(node)
              }
            })
          }

          if (mutation.removedNodes) {
            mutation.removedNodes.forEach((node) => {
              if (nodeCondition(node)) {
                observer.disconnect()
                resolve(node)
              }
            })
          }
        })
      }
    })

    // The subtree & childlist options ensure we also observe changes to
    // the descendants of the root node.
    domObserver.observe(rootNode, { subtree: true, childList: true })

    getGlobalInterruptPromise().then((exception) => {
      domObserver.disconnect()
      reject(exception)
    })
  })
}

/**
 * Observe the specified node for changes in the specified attribute;
 * resolve the promise once a change has been observed.
 *
 * The observer is removed once a change is observed.
 */
function getAttrPromise(
  node: Node,
  attribute: string,
): Promise<MutationRecord> {
  return new Promise((resolve, reject) => {
    const attrObserver = new MutationObserver((mutations, observer) => {
      mutations.forEach((mutation) => {
        observer.disconnect()
        resolve(mutation)
      })
    })

    attrObserver.observe(node, {
      attributes: true,
      attributeFilter: [attribute],
    })

    getGlobalInterruptPromise().then((exception) => {
      attrObserver.disconnect()
      reject(exception)
    })
  })
}

function getEventListenerPromise(
  element: HTMLElement,
  eventName: string,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const listenerFn = () => {
      element.removeEventListener(eventName, listenerFn)
      resolve()
    }

    element.addEventListener(eventName, listenerFn)

    getGlobalInterruptPromise().then((exception) => {
      element.removeEventListener(eventName, listenerFn)
      reject(exception)
    })
  })
}

function getPeriodicCheck(
  waitMs: number,
  checkFn: (x: number) => boolean,
  maxTries: number | null = null,
  throwError: boolean = true,
): Promise<void> {
  return new Promise((resolve, reject) => {
    let currentTimeoutId = null

    const check = (tryIx) => {
      if (maxTries !== null && tryIx > maxTries) {
        if (throwError) {
          reject(Error('Exceeded max tries'))
        } else {
          resolve()
        }
      }

      if (checkFn(tryIx)) {
        resolve()
      } else {
        currentTimeoutId = setTimeout(() => check(tryIx + 1), waitMs)
      }
    }

    currentTimeoutId = setTimeout(() => check(1), waitMs)

    getGlobalInterruptPromise().then((exception) => {
      if (currentTimeoutId) {
        clearTimeout(currentTimeoutId)
      }
      reject(exception)
    })
  })
}

async function getWaitForElementToAppearPromise<T extends Node>(
  rootNode: Node,
  getElement: (node: Node) => T | null,
  timeoutMs: number | null = 15000,
  throwErrorIfTimeout: boolean = false,
): Promise<T> {
  const element = getElement(rootNode)
  if (element) {
    return element
  }

  const promises = []

  promises.push(
    getDomPromise(rootNode, (node) => Boolean(getElement(node))).then((node) =>
      getElement(node),
    ),
  )

  if (timeoutMs) {
    promises.push(
      throwErrorIfTimeout
        ? throwIfTimeout('Timeout waiting for element to appear', timeoutMs)
        : wait(timeoutMs),
    )
  }

  return Promise.race(promises)
}

export function getWaitForElementToDisappearPromise<T extends Node>(
  context: Node,
  getElement: (node: Node) => T | null,
  timeoutMs = 2000,
): Promise<void> {
  return new Promise((resolve, reject) => {
    let observer: MutationObserver
    let timeout: ReturnType<typeof setTimeout>

    const cleanup = () => {
      observer.disconnect()
      clearTimeout(timeout)
    }

    const check = () => {
      const el = getElement(context)
      if (!el) {
        cleanup()
        resolve()
      }
    }

    observer = new MutationObserver(() => {
      check()
    })

    observer.observe(context, { childList: true, subtree: true })

    timeout = setTimeout(() => {
      cleanup()
      reject(new Error('Timed out waiting for element to disappear'))
    }, timeoutMs)

    check() // In case it's already gone
  })
}

/**
 * Detect URL change events (that don't reload page) by:
 * - checking each time the DOM updates
 * - listening to "hashchange" and "popstate" events.
 *
 * Not perfect - no guarantee an URL change will triggering one of these events.
 */
function getUrlChangePromise(): Promise<void> {
  const initialUrl = window.location.href
  let mutationObserver
  const mutationObserverPromise = new Promise<void>((resolve) => {
    mutationObserver = new MutationObserver(() => {
      if (initialUrl !== window.location.href) {
        resolve()
      }
    })

    mutationObserver.observe(document, { childList: true, subtree: true })
  })

  let hashChangeListener
  const hashChangePromise = new Promise<void>((resolve) => {
    hashChangeListener = () => {
      resolve()
    }
    window.addEventListener('hashchange', hashChangeListener)
  })

  let popStateListener
  const popStatePromise = new Promise<void>((resolve) => {
    popStateListener = () => {
      resolve()
    }

    window.addEventListener('popstate', popStateListener)
  })

  function cleanupAllListeners() {
    try {
      mutationObserver.disconnect()
    } catch {
      // ignore
    }

    try {
      window.removeEventListener('hashchange', hashChangeListener)
    } catch {
      // ignore
    }

    try {
      window.removeEventListener('popstate', popStateListener)
    } catch {
      // ignore
    }
  }

  const globalInterruptPromise = new Promise<void>((resolve, reject) => {
    getGlobalInterruptPromise().then((exception) => {
      reject(exception)
    })
  })

  return Promise.race([
    mutationObserverPromise,
    hashChangePromise,
    popStatePromise,
    globalInterruptPromise,
  ]).finally(cleanupAllListeners)
}

/**
 * Wait for a message meeting message condition to be received;
 * We remove the listener once the promise is resolved.
 */
function getMessagePromise(
  messageCondition: (message: object) => boolean,
): Promise<object> {
  return new Promise((resolve, reject) => {
    const listener = (message, sender, sendResponse) => {
      if (messageCondition(message)) {
        chrome.runtime.onMessage.removeListener(listener)
        sendResponse({ success: true })
        resolve(message)
      }
    }

    chrome.runtime.onMessage.addListener(listener)

    getGlobalInterruptPromise().then((exception) => {
      chrome.runtime.onMessage.removeListener(listener)
      reject(exception)
    })
  })
}

export {
  getAttrPromise,
  getDomPromise,
  getEventListenerPromise,
  getMessagePromise,
  getNullPromise,
  getPeriodicCheck,
  getUrlChangePromise,
  getWaitForElementToAppearPromise,
  throwIfTimeout,
  wait,
}
